import type { FetchResult } from '@apollo/client';

import { useResponseHandler } from '@hooks/useResponseHandler';

import {
  useDeleteDiscernDocumentMutation,
  useStartAutoCurationMutation,
  useUpdateDiscernDocumentMutation,
} from 'generated/discern.generated';
import type {
  DeleteDiscernDocumentMutation,
  DeleteDiscernDocumentMutationVariables,
  StartAutoCurationMutation,
  StartAutoCurationMutationVariables,
  UpdateDiscernDocumentMutation,
  UpdateDiscernDocumentMutationVariables,
} from 'generated/discern.generated';

export function useUpdateDiscernDocument(): [
  (
    params: UpdateDiscernDocumentMutationVariables['input']
  ) => Promise<FetchResult<UpdateDiscernDocumentMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = useUpdateDiscernDocumentMutation();
  const loading = baseLoading;

  async function updateDiscernDocument(
    params: UpdateDiscernDocumentMutationVariables['input']
  ) {
    const response = await mutate({
      variables: { input: { ...params } },
      update: (cache, { data: response }) => {
        if (!response?.updateDiscernDocument) {
          return;
        }

        const discernDocumentId = params.discern_document_id;
        const updatedGroupingState = params.grouping_state_json;
        const updatedAt = new Date().toISOString();

        const normalizedId = cache.identify({
          __typename: 'DiscernDocumentModel',
          discern_document_id: discernDocumentId,
        });

        if (normalizedId) {
          cache.modify({
            id: normalizedId,
            fields: {
              grouping_state_json: () => updatedGroupingState,
              updated_at: () => updatedAt,
            },
          });
        }
      },
    });
    return response;
  }

  return [updateDiscernDocument, loading];
}
export function useDeleteDiscernDocument(): [
  (
    params: DeleteDiscernDocumentMutationVariables['input']
  ) => Promise<FetchResult<DeleteDiscernDocumentMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'deleteDiscernDocument',
  });

  const [mutate, { loading }] = useDeleteDiscernDocumentMutation();

  async function deleteDiscernDocument(
    params: DeleteDiscernDocumentMutationVariables['input']
  ): Promise<FetchResult<DeleteDiscernDocumentMutation>> {
    const response = await mutate({
      variables: { input: { ...params } },
      update: (cache, { data }) => {
        const deleted = data?.deleteDiscernDocument;
        if (!deleted?.discern_document_id) return;

        const normalizedId = cache.identify({
          discern_document_id: deleted.discern_document_id,
          __typename: 'DiscernDocumentModel',
        });

        if (normalizedId) {
          cache.evict({ id: normalizedId });
          cache.gc();
        }
      },
    });

    if (response.errors) {
      handleError(response.errors);
    } else {
      handleSuccess();
    }

    return response;
  }

  return [deleteDiscernDocument, loading];
}

export function useStartAutoCuration(): [
  (
    params: StartAutoCurationMutationVariables['input']
  ) => Promise<FetchResult<StartAutoCurationMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'startAutoCuration',
  });

  const [mutate, { loading }] = useStartAutoCurationMutation();

  async function startAutoCuration(
    params: StartAutoCurationMutationVariables['input']
  ): Promise<FetchResult<StartAutoCurationMutation>> {
    // No refetch needed. The mutation returns the whole DiscernDocumentModel
    // including status and can_retry, and the cache keys that type on
    // discern_document_id (apollo/cache.ts), so the list row picks up the new
    // values from the normalized entity on this round trip.
    const response = await mutate({ variables: { input: { ...params } } });

    if (response.errors) {
      handleError(response.errors);
    } else {
      handleSuccess();
    }

    return response;
  }

  return [startAutoCuration, loading];
}
